/**
 * core/db.ts · sql.js + IndexedDB 镜像
 *
 * 设计原则:
 *   - sql.js (WASM) 客户端 SQL · 镜像 D1 schema
 *   - IndexedDB 持久化 (blob 形式) · 防刷新丢数据
 *   - 单例 db · 复用 (不再多 IIFE 抢 db)
 *   - schema migration 走 src/core/schema/ 统一管 (复用 v18.9.197 schema-runner 思路)
 */

import initSqlJs, { Database } from 'sql.js';
// Vite asset import (编译时 wasm 走 asset 管线 · 不需 CDN 不需 public/wasm/ 手动复制)
// @ts-ignore - Vite 处理 ?url suffix
import wasmUrl from 'sql.js/dist/sql-wasm.wasm?url';
import { runMigrations } from './schema/runner';
import { runSeed } from './schema/seed';

const IDB_NAME = 'trungnam-mes-pro';
const IDB_STORE = 'sqlite-blob';
const IDB_KEY = 'main';

let _db: Database | null = null;

/**
 * 获取单例 db (核心 API · 业务代码全调用 getDb())
 */
export function getDb(): Database {
  if (!_db) throw new Error('[db] getDb() called before initDb()');
  return _db;
}

/**
 * Test only · integration 测试注入内存 db
 * 生产代码不要调
 */
export function __setDbForTest__(db: Database | null): void {
  _db = db;
}

/**
 * 启动时初始化 db · main.ts 调用
 */
export async function initDb(): Promise<void> {
  // 1. 加载 sql.js WASM (Vite asset · 编译时处理 · 0 网络依赖)
  const SQL = await initSqlJs({
    locateFile: () => wasmUrl,
  });

  // 2. 尝试从 IndexedDB 恢复
  const blob = await loadFromIndexedDB();
  if (blob) {
    _db = new SQL.Database(new Uint8Array(blob));
    // eslint-disable-next-line no-console
    console.log('[db] restored from IndexedDB', blob.byteLength, 'bytes');
  } else {
    _db = new SQL.Database();
    // eslint-disable-next-line no-console
    console.log('[db] new empty database');
  }

  // 3. 运行 schema migration (幂等 · CREATE TABLE IF NOT EXISTS + ALTER try/catch)
  await runMigrations(_db);

  // 4. 第一次启动 seed (employees 40 账号 + 业务示范数据 · 让登录能跑)
  await runSeed(_db);

  // 5. 持久化到 IndexedDB (防刷新丢)
  await saveDb();
}

/**
 * 持久化 db 到 IndexedDB · 业务代码改完调用
 */
export async function saveDb(): Promise<void> {
  if (!_db) return;
  const data = _db.export();
  await saveToIndexedDB(data.buffer as ArrayBuffer);
}

/**
 * 安全查询 helper · try/catch + 返 [] on error
 */
export function query<T = Record<string, unknown>>(sql: string, params?: unknown[]): T[] {
  if (!_db) return [];
  try {
    const stmt = _db.prepare(sql);
    if (params) stmt.bind(params as any);
    const rows: T[] = [];
    while (stmt.step()) rows.push(stmt.getAsObject() as T);
    stmt.free();
    return rows;
  } catch (err) {
    console.error('[db.query]', err, '\nSQL:', sql);
    return [];
  }
}

/**
 * 安全数字查询 (COUNT/SUM) · 返 0 on error
 */
export function queryNumber(sql: string, params?: unknown[]): number {
  const rows = query<Record<string, number>>(sql, params);
  if (!rows[0]) return 0;
  const k = Object.keys(rows[0])[0];
  return Number(rows[0][k]) || 0;
}

// ============================================================
// IndexedDB helper (private)
// ============================================================
function loadFromIndexedDB(): Promise<ArrayBuffer | null> {
  // Node.js test env 跳过
  if (typeof indexedDB === 'undefined') return Promise.resolve(null);
  return new Promise((resolve) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(IDB_STORE);
    };
    req.onsuccess = () => {
      const tx = req.result.transaction(IDB_STORE, 'readonly');
      const store = tx.objectStore(IDB_STORE);
      const getReq = store.get(IDB_KEY);
      getReq.onsuccess = () => resolve((getReq.result as ArrayBuffer) || null);
      getReq.onerror = () => resolve(null);
    };
    req.onerror = () => resolve(null);
  });
}

function saveToIndexedDB(buf: ArrayBuffer): Promise<void> {
  // Node.js test env 跳过 IndexedDB
  if (typeof indexedDB === 'undefined') return Promise.resolve();
  return new Promise((resolve) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onsuccess = () => {
      const tx = req.result.transaction(IDB_STORE, 'readwrite');
      const store = tx.objectStore(IDB_STORE);
      store.put(buf, IDB_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    };
    req.onerror = () => resolve();
  });
}
